import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export interface StaffDisputeSummary {
    claimNumber: string;
    customerId?: string;
    customerName?: string;
    companyId?: string;
    divisionId?: string;
    whsId?: string;
    orderNumber: string;
    invoiceNumber?: string;
    status: string;
    claimType: string;
    totalClaimQty?: number;
    createdAt?: string;
}

export interface StaffDisputeLine {
    id?: number;
    orderLineId: number;
    itemNumber: string;
    itemName: string;
    orderedQty: number;
    claimedQty: number;
    approvedQty?: number;
    reason: string;
    resolutionType?: string;
    unitPriceEur?: number;
    claimAmountEur?: number;
}

export interface StaffDisputeAttachment {
    id: number;
    claimLineId?: number;
    originalFileName: string;
    contentType: string;
    fileSize: number;
    uploadedAt?: string;
}

export interface StaffDisputeStatusEvent {
    oldStatus?: string;
    newStatus: string;
    comment?: string;
    changedBy?: string;
    changedAt?: string;
}

export interface StaffDisputeCreditNoteLink {
    creditNoteOrderNumber: string;
    creditNoteOrderType: string;
    createdAt?: string;
    createdBy?: string;
}

export interface StaffDisputeDetail {
    claimNumber: string;
    orderNumber: string;
    invoiceNumber?: string;
    status: string;
    claimType: string;
    customerId?: string;
    customerName?: string;
    customerComment?: string;
    totalClaimQty?: number;
    totalClaimAmountEur?: number;
    createdAt?: string;
    updatedAt?: string;
    creditNoteOrderNumber?: string;
    creditNoteOrderType?: string;
    creditNoteCreatedAt?: string;
    /** All linked credit note orders (oldest first). */
    creditNotes?: StaffDisputeCreditNoteLink[];
    lines: StaffDisputeLine[];
    attachments: StaffDisputeAttachment[];
    timeline: StaffDisputeStatusEvent[];
}

@Injectable({ providedIn: 'root' })
export class StaffDisputeService {
    private readonly baseUrl = `${environment.apiUrl}staff/disputes`;

    constructor(private readonly http: HttpClient) {}

    listClaims(scope: { companyId: string; divisionId: string; whsId: string }, status?: string): Observable<StaffDisputeSummary[]> {
        let params = new HttpParams()
            .set('companyId', scope.companyId)
            .set('divisionId', scope.divisionId)
            .set('whsId', scope.whsId);
        if (status && status !== 'ALL') {
            params = params.set('status', status);
        }
        return this.http.get<StaffDisputeSummary[]>(this.baseUrl, { params });
    }

    getClaim(claimNumber: string, scope: { companyId: string; divisionId: string; whsId: string }): Observable<StaffDisputeDetail> {
        const params = new HttpParams()
            .set('companyId', scope.companyId)
            .set('divisionId', scope.divisionId)
            .set('whsId', scope.whsId);
        return this.http.get<StaffDisputeDetail>(`${this.baseUrl}/${encodeURIComponent(claimNumber)}`, { params });
    }

    updateStatus(
        claimNumber: string,
        scope: { companyId: string; divisionId: string; whsId: string },
        payload: { status: string; customerMessage?: string; internalComment?: string }
    ): Observable<StaffDisputeDetail> {
        const params = new HttpParams()
            .set('companyId', scope.companyId)
            .set('divisionId', scope.divisionId)
            .set('whsId', scope.whsId);
        return this.http.post<StaffDisputeDetail>(
            `${this.baseUrl}/${encodeURIComponent(claimNumber)}/status`,
            payload,
            { params }
        );
    }

    linkCreditNote(
        claimNumber: string,
        scope: { companyId: string; divisionId: string; whsId: string },
        payload: { creditNoteOrderNumber: string; creditNoteOrderType: string }
    ): Observable<StaffDisputeDetail> {
        const params = new HttpParams()
            .set('companyId', scope.companyId)
            .set('divisionId', scope.divisionId)
            .set('whsId', scope.whsId);
        return this.http.post<StaffDisputeDetail>(
            `${this.baseUrl}/${encodeURIComponent(claimNumber)}/credit-note`,
            payload,
            { params }
        );
    }

    downloadAttachment(
        claimNumber: string,
        attachmentId: number,
        scope: { companyId: string; divisionId: string; whsId: string }
    ): Observable<Blob> {
        const params = new HttpParams()
            .set('companyId', scope.companyId)
            .set('divisionId', scope.divisionId)
            .set('whsId', scope.whsId);
        return this.http.get(
            `${this.baseUrl}/${encodeURIComponent(claimNumber)}/attachments/${attachmentId}`,
            { params, responseType: 'blob' }
        );
    }
}
